import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowRight, Camera, CheckCircle, Upload, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SERVICES } from '../lib/services-data';
import axios from 'axios';
import { logError } from '../lib/logger';
import { SEOHead } from '../components/SEOHead';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function StarRating({ rating, onRate, interactive = false, size = 'h-5 w-5' }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => onRate(star) : undefined}
          className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
          disabled={!interactive}
        >
          <Star
            className={`${size} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-none text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <Card className="p-5 sm:p-6 h-full">
      <div className="flex items-center justify-between mb-3">
        <StarRating rating={review.note} />
        {review.service && (
          <Badge variant="secondary" className="text-xs">{review.service}</Badge>
        )}
      </div>
      {review.titre && (
        <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-2">{review.titre}</h3>
      )}
      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
        « {review.commentaire} »
      </p>
      {review.photos && review.photos.length > 0 && (
        <div className="flex gap-2 mb-3">
          {review.photos.map((photo, i) => (
            <div key={`${review._id || review.id || 'r'}-photo-${i}`} className="w-20 h-20 rounded-lg overflow-hidden border border-border">
              <img src={photo.data} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{review.nom}</span>
        <span>{review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR') : ''}</span>
      </div>
    </Card>
  );
}

export default function TemoignagesPage() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    nom: '', email: '', telephone: '', note: 5, service: '',
    titre: '', commentaire: '', agency: 'artois.ptitservices', consent_rgpd: false, honeypot: ''
  });
  const [photos, setPhotos] = useState([null, null]);
  const [errors, setErrors] = useState({});
  const fileRef1 = useRef(null);
  const fileRef2 = useRef(null);

  const [loadError, setLoadError] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/reviews`);
      setReviews(res.data.reviews);
      setStats(res.data.stats);
    } catch (err) {
      logError('chargement des avis', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Le nom est obligatoire';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!form.telephone.trim()) e.telephone = 'Le téléphone est obligatoire';
    if (!form.commentaire.trim() || form.commentaire.length < 10) e.commentaire = 'Minimum 10 caractères';
    if (!form.consent_rgpd) e.consent_rgpd = 'Consentement obligatoire';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePhotoChange = (index, file) => {
    const newPhotos = [...photos];
    newPhotos[index] = file;
    setPhotos(newPhotos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('nom', form.nom);
      formData.append('email', form.email);
      formData.append('telephone', form.telephone);
      formData.append('note', form.note);
      formData.append('service', form.service);
      formData.append('titre', form.titre);
      formData.append('commentaire', form.commentaire);
      formData.append('agency', form.agency);
      formData.append('consent_rgpd', form.consent_rgpd);
      formData.append('honeypot', form.honeypot);
      if (photos[0]) formData.append('photo1', photos[0]);
      if (photos[1]) formData.append('photo2', photos[1]);

      const res = await axios.post(`${BACKEND_URL}/api/reviews`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setSuccess(true);
        toast.success('Avis envoyé !', { description: res.data.message });
      }
    } catch (err) {
      toast.error('Erreur', { description: "Une erreur est survenue. Veuillez réessayer." });
    } finally {
      setSubmitting(false);
    }
  };

  const renderReviews = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Chargement des avis...</p>
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="text-center py-12" data-testid="reviews-load-error">
          <p className="text-muted-foreground mb-4">Impossible de charger les avis pour le moment.</p>
          <Button variant="outline" size="sm" onClick={fetchReviews} data-testid="reviews-retry-btn">Réessayer</Button>
        </div>
      );
    }
    if (reviews.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Aucun avis publié pour le moment. Soyez le premier à partager votre expérience !</p>
        </div>
      );
    }
    return (
      <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate="visible" variants={stagger}>
        {reviews.map((review) => (
          <motion.div key={review._id} variants={fadeUp}>
            <ReviewCard review={review} />
          </motion.div>
        ))}
      </motion.div>
    );
  };

  return (
    <div>
      <SEOHead
        title="Avis clients et témoignages"
        description="Découvrez les avis et témoignages de nos clients. Note moyenne 4.7/5. Services à la personne de qualité avec Easer dédié. Partagez votre expérience avec Ptitservices.com."
        path="/temoignages"
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Témoignages</span>
          </div>
          <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Avis clients</Badge>
          <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Nos clients parlent de nous</h1>
          <p className="text-muted-foreground max-w-xl">Découvrez les témoignages de nos clients satisfaits et partagez votre expérience.</p>
          
          {stats.total > 0 && (
            <div className="flex items-center gap-4 mt-6">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-[hsl(var(--primary))]">{stats.average}</span>
                <span className="text-2xl text-muted-foreground">/5</span>
              </div>
              <StarRating rating={Math.round(stats.average)} size="h-6 w-6" />
              <span className="text-sm text-muted-foreground">({stats.total} avis)</span>
            </div>
          )}
        </div>
      </section>

      {/* Reviews grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {renderReviews()}
        </div>
      </section>

      {/* Submit review form */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60" id="laisser-avis">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Votre avis</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Laissez votre témoignage</h2>
            <p className="text-muted-foreground">Votre avis sera publié après validation par notre équipe.</p>
          </motion.div>

          {success ? (
            <Card className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="font-['Space_Grotesk'] text-xl font-semibold mb-3">Merci pour votre avis !</h3>
              <p className="text-muted-foreground mb-6">Il sera publié après validation par notre équipe.</p>
              <Link to="/"><Button>Retour à l'accueil</Button></Link>
            </Card>
          ) : (
            <motion.form onSubmit={handleSubmit} initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Card className="p-6 sm:p-8 space-y-5">
                <input type="text" name="hp" value={form.honeypot} onChange={e => setForm(p => ({...p, honeypot: e.target.value}))} className="hidden" tabIndex={-1} autoComplete="off" />

                {/* Rating */}
                <div>
                  <Label className="mb-2 block">Votre note *</Label>
                  <StarRating rating={form.note} onRate={(n) => setForm(p => ({...p, note: n}))} interactive size="h-8 w-8" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nom">Nom / Prénom *</Label>
                    <Input id="nom" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Votre nom" className="mt-1" />
                    {errors.nom && <p className="text-xs text-destructive mt-1">{errors.nom}</p>}
                  </div>
                  <div>
                    <Label htmlFor="telephone">Téléphone * <span className="text-xs text-muted-foreground">(obligatoire)</span></Label>
                    <Input id="telephone" value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} placeholder="06 00 00 00 00" className="mt-1" />
                    {errors.telephone && <p className="text-xs text-destructive mt-1">{errors.telephone}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email * <span className="text-xs text-muted-foreground">(obligatoire, non affiché)</span></Label>
                  <Input id="email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="votre@email.com" className="mt-1" />
                  {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                </div>

                <div>
                  <Label>Agence *</Label>
                  <Select value={form.agency} onValueChange={v => setForm(p => ({...p, agency: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="artois.ptitservices">Agence Artois</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Service concerné</Label>
                  <Select value={form.service} onValueChange={v => setForm(p => ({...p, service: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionnez (optionnel)" /></SelectTrigger>
                    <SelectContent>
                      {SERVICES.map(s => (
                        <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="titre">Titre de votre avis</Label>
                  <Input id="titre" value={form.titre} onChange={e => setForm(p => ({...p, titre: e.target.value}))} placeholder="En quelques mots..." className="mt-1" />
                </div>

                <div>
                  <Label htmlFor="commentaire">Votre témoignage *</Label>
                  <Textarea id="commentaire" value={form.commentaire} onChange={e => setForm(p => ({...p, commentaire: e.target.value}))} placeholder="Partagez votre expérience avec Ptitservices..." className="mt-1" rows={4} />
                  {errors.commentaire && <p className="text-xs text-destructive mt-1">{errors.commentaire}</p>}
                </div>

                {/* Photos */}
                <div>
                  <Label className="mb-2 block">Photos (optionnel, max 2)</Label>
                  <div className="flex gap-3">
                    {[0, 1].map((idx) => (
                      <div key={`photo-slot-${idx}`} className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={idx === 0 ? fileRef1 : fileRef2}
                          onChange={(e) => handlePhotoChange(idx, e.target.files[0])}
                        />
                        <button
                          type="button"
                          onClick={() => (idx === 0 ? fileRef1 : fileRef2).current?.click()}
                          className="w-24 h-24 rounded-xl border-2 border-dashed border-border hover:border-[hsl(var(--primary))] transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-[hsl(var(--primary))] overflow-hidden"
                        >
                          {photos[idx] ? (
                            <img src={URL.createObjectURL(photos[idx])} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <>
                              <Camera className="h-5 w-5" />
                              <span className="text-[10px]">Photo {idx + 1}</span>
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Format JPG, PNG. Max 5 Mo par photo.</p>
                </div>

                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox checked={form.consent_rgpd} onCheckedChange={v => setForm(p => ({...p, consent_rgpd: v}))} className="mt-0.5" />
                    <span className="text-xs text-muted-foreground">J'accepte que mes données soient utilisées pour publier mon avis. Mon email et téléphone ne seront pas affichés publiquement. *</span>
                  </label>
                  {errors.consent_rgpd && <p className="text-xs text-destructive mt-1">{errors.consent_rgpd}</p>}
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                  {submitting ? 'Envoi en cours...' : 'Publier mon avis'}
                  <Star className="ml-2 h-4 w-4" />
                </Button>
              </Card>
            </motion.form>
          )}
        </div>
      </section>
    </div>
  );
}
